import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { OAuthService } from 'angular-oauth2-oidc';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { User } from '@app/shared/models';
import { RoleName } from '@app/shared/enums/user.enum';
import { dropdownItems } from '@app/components/header/constants';
import { HeaderComponent } from '@app/components/header/header.component';
import { DropdownItemType } from '@app/components/header/enums';
import { NotificationsStore } from '@app/pages/notifications/store/notifications.store';
import { LanguagesMenu } from '@app/components/header/constants/languages-menu.constant';
import { DateAdapter } from '@angular/material/core';
import moment from 'moment';
import { CompanyStatus } from '@app/shared/enums';

@Component({
  selector: 'app-side-bar-menu',
  templateUrl: './side-bar-menu.component.html',
  styleUrl: './side-bar-menu.component.scss',
  imports: [
    MatButton,
    TranslatePipe,
    CommonModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideBarMenuComponent implements OnInit {
  @Input() userInfo: User;
  @Input() userRoles: RoleName[] = [];

  @Output() closeSideBarMenu: EventEmitter<void> = new EventEmitter<void>();

  public notificationCountUnread$ =
    this.notificationsStore.notificationCountUnread$;

  public currentLang = 'uk';
  public languagesLists = LanguagesMenu;

  public readonly roleName = RoleName;
  public readonly dropdownItems = dropdownItems;
  public readonly companyStatus = CompanyStatus;

  constructor(
    private oAuthService: OAuthService,
    private router: Router,
    private translate: TranslateService,
    private destroyRef: DestroyRef,
    public headerComponent: HeaderComponent,
    private notificationsStore: NotificationsStore,
    private dateAdapter: DateAdapter<any>
  ) {}

  public get roles(): string {
    const roles = this.userInfo.roles.map(role => role.name);

    return roles.includes(RoleName.Admin) ? RoleName.Admin : roles.join(', ');
  }

  ngOnInit() {
    this.getLocationUser();
  }

  public logout(): void {
    sessionStorage.removeItem('lastVisitedUrl');
    sessionStorage.removeItem('lastUserId');

    this.oAuthService.logOut();
    this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
  }

  public closePopup(): void {
    this.closeSideBarMenu.next();
  }

  public selectCurrentLanguage(lang: string): void {
    if (this.currentLang === lang) return;

    this.currentLang = lang;
    localStorage.setItem('currentLanguage', lang);
    this.translate
      .use(lang)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dateAdapter.setLocale(lang);
        moment.locale(lang);

        location.reload();
      });
  }

  public navigationToPage(url: string): void {
    this.router.navigate([url], { queryParamsHandling: 'merge' });
    this.closePopup();
  }

  public selectItem(type: DropdownItemType): void {
    this.headerComponent.selectItem(type);
  }

  public goToProfile(): void {
    this.router.navigate(['/company/details']);
    this.closePopup();
  }

  private getLocationUser(): void {
    const currentLanguage = localStorage.getItem('currentLanguage')!;
    if (currentLanguage) {
      this.currentLang = currentLanguage;
      this.translate.use(currentLanguage);
    } else {
      const browserLang = this.translate.getBrowserLang();
      const language = LanguagesMenu.find(lang => lang.uniCode === browserLang);

      if (!language) {
        this.translate.use(this.currentLang);
        localStorage.setItem('currentLanguage', this.currentLang);
      } else {
        localStorage.setItem('currentLanguage', language.uniCode);
        this.translate.use(language.uniCode);
      }
    }
  }
}
